// src/encounter/services/use-sign-lock.ts
// Encounter Sign & Lock (PROD-1282).
//   GET  /api/Patients/GetSignLockByPatientId?EncounterId=&PatientId= → SignLockModel[]
//   POST /api/Patients/CreateSignLock  (body = SignLockModel)
// Verified routed: 200 + [] on dev-api. Creating a lock=true record is what the
// backend turns into the EncounterSignLock event inventory consumes (PROD-1168/1169).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, ApiError } from '@/shared/api/api-client';
import type { SignLockModel } from '@/types/sign-lock-api';
import { advanceCareModelsAfterSign } from './advance-care-models-on-sign';
import { notifyLabOrderAuthorized } from './l360-order-authorized';

export function signLockKey(encounterId: string | null | undefined, patientId: string | null | undefined) {
  return ['sign-lock', encounterId, patientId] as const;
}

/** Read the encounter's sign-lock records (404 → [], mirror Flutter). */
export function useSignLock(
  encounterId: string | null | undefined,
  patientId: string | null | undefined,
) {
  return useQuery<SignLockModel[]>({
    queryKey: signLockKey(encounterId, patientId),
    enabled: !!encounterId && !!patientId,
    queryFn: async ({ signal }) => {
      // Narrow (not cast) — `enabled` already guards, this keeps the URL from ever
      // being built with "undefined" segments and drops the `as string` casts.
      if (!encounterId || !patientId) return [];
      try {
        return await get<SignLockModel[]>(
          `/api/Patients/GetSignLockByPatientId?EncounterId=${encodeURIComponent(
            encounterId,
          )}&PatientId=${encodeURIComponent(patientId)}`,
          { signal },
        );
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return [];
        throw e;
      }
    },
  });
}

/** Create a sign-lock record (sign, or sign+lock). Refreshes the sign-lock query. */
export function useCreateSignLock(
  encounterId: string | null | undefined,
  patientId: string | null | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SignLockModel) => post<string>('/api/Patients/CreateSignLock', body),
    onSuccess: (id, signed) => {
      // The sign has landed; move the patient's care model on. Fire-and-report — returns nothing,
      // so this mutation settles without waiting on Scrips.Workflows (see the module header).
      advanceCareModelsAfterSign(qc, signed);
      // Same shape: tell Longevity 360 which lab orders the physician just authorised. Fire-and-report, dev flag.
      notifyLabOrderAuthorized(id, signed);
      void qc.invalidateQueries({ queryKey: signLockKey(encounterId, patientId) });
      // SIGNING MOVES TWO RECORDS THE CLIENT NEVER WRITES, so both reads must be re-fetched or the
      // UI keeps showing the pre-sign state until a reload (Andrew, 2026-08-27: "I pressed sign and
      // lock in an encounter and add diagnosis and the appointment still arrived").
      //
      // Observed on dev, the same pairing on two appointments:
      //   encounter Draft     <-> appointment Arrived
      //   encounter Completed <-> appointment Fulfilled
      // and the Fulfilled definition itself reads "…the encounter resource will exist…". Neither
      // transition is written by this app OR by Flutter — every Flutter reference to
      // `encounterStatusCompleted` is a READ in pre_encounter_widget.dart, and its
      // `updateStatusUseCase` has exactly one call site (the status dropdown). So the BACKEND
      // performs both on CreateSignLock, and the client's only job is to re-read.
      //
      // Without these two lines the appointment pill and the primary action (Continue vs Edit) both
      // stay stale after signing — the same write-works/read-stale shape as PROD-2030.
      void qc.invalidateQueries({ queryKey: ['appointment-details'] });
      void qc.invalidateQueries({ queryKey: ['encounter-by-appointment'] });
      // Andrew's walk on dev, 7 Oct: the Wrap-up pending-order rows still said "Not signed" after the sign — their
      // `signed` flag is the SERVER's (the invoice orders read), which the sign changes and nothing re-read. A refetch
      // only (switch ON and OFF; listed with the intended OFF changes).
      void qc.invalidateQueries({ queryKey: ['invoice', 'orders'] });
    },
  });
}
